import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Plus, ChevronRight, Loader2, Lightbulb } from "lucide-react";
import { campaignAPI, guestAPI } from "../services/api";
import { Guest } from "../types";

interface CampaignRow {
  id: string;
  title: string;
  body: string;
  status: string;
  audience_type: string;
  category?: string | null;
  eligible_count?: number;
  created_at: string;
  scheduled_at?: string | null;
}

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [segments, setSegments] = useState<{ id: string; name: string }[]>([]);
  const [suggestions, setSuggestions] = useState<{
    campaigns: Array<{
      id: string;
      title: string;
      body: string;
      category: string;
      audience_type: "all_checked_in" | "segment" | "manual";
      segment_hint?: string;
      rationale: string;
    }>;
    context: {
      checked_in_marketing: number;
      segment_count: number;
      campaign_draft_count: number;
    };
  } | null>(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const [form, setForm] = useState({
    title: "",
    body: "",
    audience_type: "all_checked_in" as "all_checked_in" | "segment" | "manual",
    segment_id: "" as string,
    category: "",
    manual_guest_ids: [] as string[],
  });

  const load = async () => {
    try {
      const tasks: Promise<unknown>[] = [
        campaignAPI.list(),
        campaignAPI.getSuggestions().catch(() => null),
      ];
      if (isAdmin) {
        tasks.push(campaignAPI.listSegments().catch(() => []));
      }
      const results = await Promise.all(tasks);
      const data = results[0] as CampaignRow[];
      const sug = results[1] as Awaited<ReturnType<typeof campaignAPI.getSuggestions>> | null;
      const seg = (isAdmin ? results[2] : null) as { id: string; name: string }[] | null;
      setCampaigns(data);
      if (sug) setSuggestions(sug);
      if (isAdmin && Array.isArray(seg)) setSegments(seg);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const applyCampaignTemplate = async (idea: NonNullable<typeof suggestions>["campaigns"][0]) => {
    let segList = segments;
    if (segList.length === 0) {
      try {
        segList = await campaignAPI.listSegments();
        setSegments(segList);
      } catch {
        /* ignore */
      }
    }
    let segment_id = "";
    if (idea.audience_type === "segment" && idea.segment_hint) {
      const m = segList.find((s) => s.name === idea.segment_hint);
      if (m) segment_id = m.id;
    }
    setForm({
      title: idea.title,
      body: idea.body,
      audience_type: idea.audience_type,
      segment_id,
      category: idea.category,
      manual_guest_ids: [],
    });
    setShowModal(true);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!showModal) return;
    (async () => {
      try {
        const [g, s] = await Promise.all([
          guestAPI.getAllGuests(),
          campaignAPI.listSegments(),
        ]);
        setGuests(
          g.filter(
            (x) =>
              x.status === "checked_in" && x.marketing_opt_in !== false
          )
        );
        setSegments(s);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [showModal]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await campaignAPI.create({
        title: form.title,
        body: form.body,
        audience_type: form.audience_type,
        segment_id: form.segment_id || null,
        category: form.category || null,
        manual_guest_ids:
          form.audience_type === "manual" ? form.manual_guest_ids : undefined,
      });
      setShowModal(false);
      setForm({
        title: "",
        body: "",
        audience_type: "all_checked_in",
        segment_id: "",
        category: "",
        manual_guest_ids: [],
      });
      await load();
    } catch (err) {
      console.error(err);
      alert("Could not create campaign");
    } finally {
      setSaving(false);
    }
  };

  const statusColor = (s: string) => {
    switch (s) {
      case "completed":
        return "text-emerald-400";
      case "sending":
        return "text-amber-400";
      case "draft":
        return "text-slate-400";
      case "scheduled":
        return "text-sky-600";
      default:
        return "text-grey-guestby";
    }
  };

  return (
    <div className="min-h-full p-8 bg-slate-50">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-black-guestby flex items-center gap-2">
              <Megaphone className="h-7 w-7 text-blue-guestby" />
              Campaigns
            </h1>
            <p className="text-grey-guestby mt-1 text-sm">
              WhatsApp broadcasts to checked-in guests — track replies and
              sentiment.
            </p>
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-guestby text-black-guestby font-medium hover:opacity-90"
            >
              <Plus className="h-4 w-4" />
              New campaign
            </button>
          )}
        </div>

        {isAdmin && suggestions && suggestions.campaigns.length > 0 && (
          <div className="mb-8 rounded-xl border border-amber-200/80 bg-amber-50/90 p-5">
            <h2 className="text-sm font-semibold text-black-guestby flex items-center gap-2 mb-1">
              <Lightbulb className="h-4 w-4 text-amber-600" />
              Suggested campaigns
            </h2>
            <p className="text-xs text-slate-600 mb-4">
              Pick a template to pre-fill a draft. You can edit before saving.{" "}
              {suggestions.context.checked_in_marketing > 0 && (
                <>
                  <span className="font-medium text-black-guestby">
                    {suggestions.context.checked_in_marketing}
                  </span>{" "}
                  checked-in guests (marketing opt-in).
                </>
              )}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {suggestions.campaigns.map((idea) => (
                <div
                  key={idea.id}
                  className="rounded-lg border border-amber-200/90 bg-white p-3 text-left shadow-sm"
                >
                  <div className="text-xs uppercase text-slate-500 mb-1">{idea.category}</div>
                  <div className="font-medium text-black-guestby text-sm mb-1">{idea.title}</div>
                  <p className="text-xs text-slate-600 line-clamp-2 mb-2">{idea.rationale}</p>
                  <button
                    type="button"
                    onClick={() => applyCampaignTemplate(idea)}
                    className="text-xs font-medium text-blue-guestby hover:underline"
                  >
                    Use template
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20 text-grey-guestby">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-grey-guestby">
            <p className="mb-2">No saved campaigns yet.</p>
            <p className="text-sm text-slate-500">
              Use a suggested template above (admin) or create a draft. Demo data also seeds example
              drafts when the demo hotel exists.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-black-guestby text-grey-guestby uppercase text-xs">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Schedule</th>
                  <th className="px-4 py-3">Audience</th>
                  <th className="px-4 py-3">Recipients</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {campaigns.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-black-guestby font-medium">
                      {c.title}
                    </td>
                    <td className={`px-4 py-3 capitalize ${statusColor(c.status)}`}>
                      {c.status}
                    </td>
                    <td className="px-4 py-3 text-grey-guestby text-xs">
                      {c.scheduled_at
                        ? new Date(c.scheduled_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-grey-guestby capitalize">
                      {c.audience_type.replace(/_/g, " ")}
                    </td>
                    <td className="px-4 py-3 text-grey-guestby">
                      {c.eligible_count ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/campaigns/${c.id}`}
                        className="inline-flex items-center text-blue-guestby hover:underline"
                      >
                        View <ChevronRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl border border-slate-200">
            <h2 className="text-lg font-semibold text-black-guestby mb-4">
              New campaign
            </h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs text-grey-guestby mb-1">
                  Title
                </label>
                <input
                  required
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-black-guestby"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs text-grey-guestby mb-1">
                  Message
                </label>
                <textarea
                  required
                  rows={4}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-black-guestby"
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs text-grey-guestby mb-1">
                  Category (for analytics)
                </label>
                <input
                  placeholder="e.g. brunch, spa"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-black-guestby"
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-xs text-grey-guestby mb-1">
                  Audience
                </label>
                <select
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-black-guestby"
                  value={form.audience_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      audience_type: e.target.value as typeof form.audience_type,
                    })
                  }
                >
                  <option value="all_checked_in">All checked-in guests</option>
                  <option value="segment">Saved segment</option>
                  <option value="manual">Manual selection</option>
                </select>
              </div>
              {form.audience_type === "segment" && (
                <div>
                  <label className="block text-xs text-grey-guestby mb-1">
                    Segment
                  </label>
                  <select
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-black-guestby"
                    value={form.segment_id}
                    onChange={(e) =>
                      setForm({ ...form, segment_id: e.target.value })
                    }
                  >
                    <option value="">Select…</option>
                    {segments.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    <Link to="/campaigns/segments" className="text-blue-guestby hover:underline">
                      Create and manage segments
                    </Link>{" "}
                    (prefix and/or room list; marketing opt-in guests only).
                  </p>
                </div>
              )}
              {form.audience_type === "manual" && (
                <div>
                  <label className="block text-xs text-grey-guestby mb-2">
                    Guests
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                    {guests.map((g) => (
                      <label
                        key={g.id}
                        className="flex items-center gap-2 text-sm text-black-guestby"
                      >
                        <input
                          type="checkbox"
                          checked={form.manual_guest_ids.includes(g.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm({
                                ...form,
                                manual_guest_ids: [...form.manual_guest_ids, g.id],
                              });
                            } else {
                              setForm({
                                ...form,
                                manual_guest_ids: form.manual_guest_ids.filter(
                                  (x) => x !== g.id
                                ),
                              });
                            }
                          }}
                        />
                        {g.name} — {g.room_number}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-grey-guestby"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-blue-guestby text-black-guestby font-medium inline-flex items-center gap-2"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save draft
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
